import * as THREE from 'three'

/**
 * 把一个 source Mesh 的有效世界变换持续投影到普通 Object3D 派生对象。
 *
 * 设计说明：
 * 1. source Mesh 仍是装配位姿真相，目标对象只保存派生视觉；source 可以脱离主场景树；
 * 2. 绑定时记录目标局部坐标到 source 局部坐标的稳定后缀，后续不从名称或父链猜测；
 * 3. 目标对象可以与 source Mesh 为兄弟节点或位于不同父级，写入前统一换算到目标父级局部空间；
 * 4. 全部 Matrix4 均在实例内复用，拖动和旋转热路径不创建临时对象。
 */
export class ImportedModelInstancedObjectTransformProjection {
  /** 目标对象局部坐标到 source Mesh 局部坐标的稳定后缀矩阵。 */
  private readonly targetLocalToSourceLocalMatrix = new THREE.Matrix4()

  /** 目标父级世界矩阵的逆矩阵缓存。 */
  private readonly inverseTargetParentWorldMatrix = new THREE.Matrix4()

  /** 目标对象下一帧局部矩阵缓存。 */
  private readonly targetLocalMatrix = new THREE.Matrix4()

  /** 目标对象下一帧世界矩阵缓存。 */
  private readonly targetWorldMatrix = new THREE.Matrix4()

  /**
   * 创建普通 Object3D 变换投影。
   *
   * @param targetObject 接收派生变换的普通场景对象。
   * @param initialSourceWorldMatrix 创建绑定时 source Mesh 的有效世界矩阵。
   * @throws 目标对象尚未挂载父级时抛出错误。
   */
  public constructor(
    private readonly targetObject: THREE.Object3D,
    initialSourceWorldMatrix: THREE.Matrix4,
  ) {
    if (!targetObject.parent) {
      throw new Error('实例普通对象变换投影要求目标对象已挂载父级')
    }
    targetObject.updateWorldMatrix(true, false)
    this.targetLocalToSourceLocalMatrix
      .copy(initialSourceWorldMatrix)
      .invert()
      .multiply(targetObject.matrixWorld)
  }

  /**
   * 将 source Mesh 当前世界变换写入目标对象及其子树。
   *
   * @param sourceWorldMatrix source Mesh 当前有效世界矩阵。
   * @returns 目标对象仍有父级并完成同步时返回 true；目标已经脱离场景时返回 false。
   */
  public syncFromSourceWorldMatrix(sourceWorldMatrix: THREE.Matrix4): boolean {
    const targetParent = this.targetObject.parent
    if (!targetParent) {
      return false
    }

    /**
     * Coordinator 对变化根调用 `updateWorldMatrix(true, true)` 时已经刷新 source、目标父级和
     * 兄弟 topology host 所在子树。这里直接复用已提交矩阵，避免每个 Body 重复向上刷新父链。
     */
    this.inverseTargetParentWorldMatrix.copy(targetParent.matrixWorld).invert()
    this.targetWorldMatrix.multiplyMatrices(
      sourceWorldMatrix,
      this.targetLocalToSourceLocalMatrix,
    )
    this.targetLocalMatrix.multiplyMatrices(
      this.inverseTargetParentWorldMatrix,
      this.targetWorldMatrix,
    )
    this.targetLocalMatrix.decompose(
      this.targetObject.position,
      this.targetObject.quaternion,
      this.targetObject.scale,
    )
    this.targetObject.updateMatrix()
    this.targetObject.updateWorldMatrix(false, true)
    return true
  }
}
